import axios from "axios";
import * as aux from "../../auxilaries";
import {
    AdminGlobalSetupSections,
    AdminGlobalSetupSectionNames,
    PostedRequestDisplay,
} from '../../Enums';

export const noop = () => { };

export const rootURL = `${process.env.REACT_APP_API_DOTNET}/api/v1/admin/globalConfiguration`;

// To avoid circular dependency imports
export {
    AdminGlobalSetupSections,
    AdminGlobalSetupSectionNames,
    PostedRequestDisplay,
};

// TODO - Put this typing definitions in ~./typings

/**
 * @typedef {{
 *  archiving: { archiveInactiveDays: number };
 *  displayPreferences: {
 *      lastVisibleOrgPathLength: number;
 *      postedRequestDisplay: number;
 *      isShowOrganizationalPath: boolean;
 *  };
 *  employeeReports: { isAllEmployeeOffersEnabled: boolean; }
 * }} GlobalConfigs
 *
 *
 * @typedef {{
 * configs: GlobalConfigs;
 * unchangedConfigs: GlobalConfigs;
 * }} ILoadedConfigs
 *
 *
 * @typedef {(loadedConfigs: ILoadedConfigs) => unknown} IOnSuccess
 * @typedef {(message: string) => unknown} IOnError
 *
 * @typedef {GlobalConfigs & {
 *  configs: GlobalConfigs;
 *  unchangedConfigs: GlobalConfigs;
 *  showConfirmModal: boolean;
 *  confirmModelSection: string;
 *  typeSnackbar: string;
 *  messageSnackbar: string;
 *  openSnackbar: boolean;
 * }} InitialStateGlobalConfigurations
 *
 */

export const defaultConfigs = {
    archiving: {
        archiveInactiveDays: 0,
    },
    displayPreferences: {
        lastVisibleOrgPathLength: 0,
        postedRequestDisplay: PostedRequestDisplay.requestName,
        isShowOrganizationalPath: false
    },
    employeeReports: {
        isAllEmployeeOffersEnabled: false,
    },
    shiftSwapSetup: {
        overrideMonthsInFuture: false,
        requestPeriodNumberOfMonthsInFuture: 1,
    }
};

/** @type {InitialStateGlobalConfigurations} */
export const initialStateGlobalConfigurations = {
    configs: defaultConfigs,
    unchangedConfigs: defaultConfigs,
    showConfirmModal: false,
    confirmModelSection: '',
    typeSnackbar: 'success',
    messageSnackbar: '',
    openSnackbar: false,
};

export const getUpdatedStateWithUnchangedConfigs = (unchangedConfigs, updatedState) => ({
    unchangedConfigs: {
        ...unchangedConfigs,
        ...updatedState,
    },
    showConfirmModal: false,
    confirmModelSection: '',
});

/**
 * @returns {{
 *  route: string;
 *  data: *;
 *  globalConfigToUpdate: {
 *     unchangedConfigs: *;
 *     showConfirmModal: boolean;
 *     confirmModelSection: string;
 * };
 * }|undefined}
 */
export const getGlobalConfigParamsToUpdate = (
    unchangedConfigs,
    confirmModelSection,
    AdminGlobalSetupSections,
    AdminGlobalSetupSectionNames,
    configs,
) => {
    const params = {};

    if (confirmModelSection === AdminGlobalSetupSections.archiving) {
        params.route = AdminGlobalSetupSectionNames.archiving;
        params.data = { archiveDays: configs.archiving.archiveInactiveDays }
        params.globalConfigToUpdate = getUpdatedStateWithUnchangedConfigs(unchangedConfigs, { archiving: { ...configs.archiving } });
    } else if (confirmModelSection === AdminGlobalSetupSections.displayPreferences) {
        params.route = AdminGlobalSetupSectionNames.displayPreferences;
        params.data = { ...configs.displayPreferences };
        params.globalConfigToUpdate = getUpdatedStateWithUnchangedConfigs(unchangedConfigs, { displayPreferences: { ...configs.displayPreferences } });
    } else if (confirmModelSection === AdminGlobalSetupSections.employeeReports) {
        params.route = AdminGlobalSetupSectionNames.employeeReports;
        params.data = { ...configs.employeeReports };
        params.globalConfigToUpdate = getUpdatedStateWithUnchangedConfigs(unchangedConfigs, { employeeReports: { ...configs.employeeReports } });
    } else if (confirmModelSection === AdminGlobalSetupSections.shiftSwapSetup) {
        params.route = AdminGlobalSetupSectionNames.shiftSwapSetup;
        params.data = { ...configs.shiftSwapSetup };
        params.globalConfigToUpdate = getUpdatedStateWithUnchangedConfigs(unchangedConfigs, { shiftSwapSetup: { ...configs.shiftSwapSetup } });
    }

    return Object.keys(params).length ? params : undefined;
};


export const getGlobalConfigurations = (
    options,
    /** @type {IOnSuccess} */
    onSuccess = noop,
    /** @type {IOnError} */
    onError = noop,
) => axios.get(rootURL, options)
    .then(({ data }) => {
        if (!data) {
            return;
        }

        const {
            archiveDays,
            isAllEmployeeOffersEnabled,
            isShowOrganizationalPath,
            lastVisibleOrgPathLength,
            postedRequestDisplay,
            requestPeriodNumberOfMonthsInFuture,
        } = data;

        onSuccess({
            configs: {
                archiving: {
                    archiveInactiveDays: archiveDays,
                },
                displayPreferences: {
                    lastVisibleOrgPathLength: lastVisibleOrgPathLength,
                    postedRequestDisplay: postedRequestDisplay,
                    isShowOrganizationalPath: isShowOrganizationalPath
                },
                employeeReports: {
                    isAllEmployeeOffersEnabled,
                },
                shiftSwapSetup: {
                    overrideMonthsInFuture: requestPeriodNumberOfMonthsInFuture !== defaultConfigs.shiftSwapSetup.requestPeriodNumberOfMonthsInFuture,
                    requestPeriodNumberOfMonthsInFuture
                }
            },
            unchangedConfigs: {
                archiving: {
                    archiveInactiveDays: archiveDays,
                },
                displayPreferences: {
                    lastVisibleOrgPathLength: lastVisibleOrgPathLength,
                    postedRequestDisplay: postedRequestDisplay,
                    isShowOrganizationalPath: isShowOrganizationalPath
                },
                employeeReports: {
                    isAllEmployeeOffersEnabled,
                },
                shiftSwapSetup: {
                    overrideMonthsInFuture: requestPeriodNumberOfMonthsInFuture !== defaultConfigs.shiftSwapSetup.requestPeriodNumberOfMonthsInFuture,
                    requestPeriodNumberOfMonthsInFuture
                }
            }
        });
    })
    .catch((err) => {
        const message = aux.handleAPIErrors(err.response);
        onError(message);
    });

export const updateGlobalConfiguration = (
    endpoint,
    data,
    options,
    onSuccess = noop,
    /** @type {IOnError} */
    onError = noop,
) => axios.put(`${rootURL}/${endpoint}`, data, options)
    .then(onSuccess)
    .catch((err) => {
        const message = aux.handleAPIErrors(err.response);
        onError(message);
    });
